// PricingPage.jsx - Privacy-focused features, conversion optimized
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../utils/auth';
import { CheckCircle, ArrowRight, Eye, Zap, Users, Shield, Crown, Sparkles, Target, Globe, Lock, CreditCard, TrendingUp, BarChart3 } from 'lucide-react';

export default function PricingPage() {
  const { user, getCurrentTier, upgradeTier, TIER_CONFIG } = useAuth();
  const navigate = useNavigate();
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [upgrading, setUpgrading] = useState(null);

  const handleUpgrade = async (tierKey) => {
    if (!user) {
      navigate(`/register?tier=${tierKey}`);
      return;
    }

    if (tierKey === 'observer') {
      navigate('/dashboard');
      return;
    }

    if (tierKey === 'enterprise') {
      navigate('/contact?plan=enterprise');
      return;
    }

    try {
      setUpgrading(tierKey);
      await upgradeTier(tierKey);
      navigate('/dashboard');
    } catch (error) {
      console.error('Upgrade failed:', error);
      alert('Upgrade failed. Please try again or contact support.');
    } finally {
      setUpgrading(null);
    }
  };

  const getPrice = (tierConfig) => {
    if (tierConfig.price === 0) return 'Free';
    const price = billingCycle === 'annual' ? Math.floor(tierConfig.price * 0.8) : tierConfig.price;
    return `$${price}`;
  };

  const tierData = [
    {
      key: 'observer',
      config: TIER_CONFIG.observer,
      icon: Eye,
      iconColor: 'from-gray-400 to-gray-600',
      description: "Get started with basic features",
      features: [
        "10 website analyses per month",
        "🔒 3 private analyses per month",
        "📢 Unlimited public analyses",
        "Basic scoring and insights",
        "Public feed access",
        "Community support",
        "Mobile-responsive reports"
      ],
      cta: user?.tier === 'observer' ? 'Current Plan' : 'Start Free',
      popular: false,
      badge: null,
      valueProps: "Perfect for testing • Great for students"
    },
    {
      key: 'professional',
      config: TIER_CONFIG.professional,
      icon: Zap,
      iconColor: 'from-blue-400 to-cyan-500',
      description: "Everything you need to excel",
      features: [
        "🚀 Unlimited website analyses",
        "🔒 Unlimited private analyses",
        "📊 Advanced AI insights & recommendations",
        "🔧 API access (1,000 calls/month)",
        "📄 Export reports & data",
        "📧 Email alerts for opportunities",
        "⚡ Priority support response",
        "🎯 Competitive analysis tools",
        "🔍 SEO keyword insights"
      ],
      cta: user?.tier === 'professional' ? 'Current Plan' : 'Get Professional',
      popular: true,
      badge: 'MOST POPULAR',
      valueProps: 'Save 40+ hours/month • See ROI immediately'
    },
    {
      key: 'business',
      config: TIER_CONFIG.business,
      icon: Users,
      iconColor: 'from-green-400 to-emerald-500',
      description: "Scale your agency operations",
      features: [
        "✅ Everything in Professional",
        "🏷️ White-label reports with your branding",
        "👥 Team collaboration (10 seats)",
        "🔗 Custom integrations",
        "🤝 Dedicated account manager",
        "⚡ Priority analysis queue",
        "🚀 API access (10,000 calls/month)",
        "📈 Advanced analytics dashboard",
        "🔒 Team privacy controls",
        "💼 Client management tools"
      ],
      cta: user?.tier === 'business' ? 'Current Plan' : 'Get Business',
      popular: false,
      badge: 'BEST VALUE',
      valueProps: 'Perfect for agencies • Pays for itself with 2 clients'
    },
    {
      key: 'enterprise',
      config: TIER_CONFIG.enterprise,
      icon: Crown,
      iconColor: 'from-purple-400 to-pink-500',
      description: "Custom solutions for large teams",
      features: [
        "✅ Everything in Business",
        "🔒 Private by default (enterprise security)",
        "🧠 Custom AI model training",
        "🚀 Unlimited API access",
        "🏗️ Dedicated infrastructure",
        "📋 SLA guarantees (99.9% uptime)",
        "🎓 Onboarding & training",
        "👥 Unlimited team seats",
        "🛡️ Compliance & security customization",
        "🔐 SSO & enterprise auth",
        "📊 Custom reporting & analytics"
      ],
      cta: user?.tier === 'enterprise' ? 'Current Plan' : 'Contact Sales',
      popular: false,
      badge: 'ENTERPRISE',
      valueProps: 'Tailored to your needs • Full customization'
    }
  ];

  const currentTier = getCurrentTier();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 pricing-page">
      {/* Public Navigation */}
      {!user && (
        <nav className="bg-slate-900/80 backdrop-blur-xl border-b border-slate-700/50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-4">
              <Link to="/landing" className="flex items-center space-x-4">
                <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
                  <Eye className="w-6 h-6 text-white" />
                </div>
                <span className="text-2xl font-bold text-white">CodeRated</span>
              </Link>
              
              <div className="hidden md:flex items-center space-x-8">
                <Link to="/landing" className="text-slate-300 hover:text-white transition-colors">Home</Link>
                <Link to="/feed" className="text-slate-300 hover:text-white transition-colors">Browse</Link>
                <Link to="/pricing" className="text-white font-semibold">Pricing</Link>
              </div>

              <div className="flex items-center space-x-4">
                <Link to="/login" className="text-slate-300 hover:text-white transition-colors">
                  Sign In
                </Link>
                <Link to="/register" className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-6 py-2 rounded-lg font-semibold hover:from-blue-600 hover:to-purple-700 transition-all">
                  Get Started
                </Link>
              </div>
            </div>
          </div>
        </nav>
      )}

      <div className="relative">
        {/* Privacy Value Banner */}
        <div className="bg-gradient-to-r from-purple-500 to-blue-500 text-white py-3 px-4 text-center">
          <div className="flex items-center justify-center gap-3">
            <Lock className="w-5 h-5" />
            <span className="font-bold">Privacy-First Platform</span>
            <span className="text-sm opacity-90">• Your competitive data stays secure</span>
            <span className="text-sm opacity-90">• Full control over what you share</span>
          </div>
        </div>

        {/* Hero Section */}
        <section className="pt-16 pb-12 px-4">
          <div className="max-w-7xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-full text-sm text-blue-200 mb-8">
              <Sparkles className="w-4 h-4" />
              <span>Trusted by developers worldwide</span>
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
            </div>

            <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-white via-blue-100 to-purple-200 bg-clip-text text-transparent mb-6">
              Choose Your Privacy Level
            </h1>
            <p className="text-xl text-slate-400 max-w-3xl mx-auto mb-6">
              Start free, upgrade for unlimited private analyses. Your competitive intelligence stays secure.
            </p>
            
            {/* Privacy Highlight */}
            <div className="max-w-2xl mx-auto bg-purple-500/10 border border-purple-500/30 rounded-xl p-4 mb-12">
              <div className="flex items-center justify-center gap-2 text-purple-300">
                <Shield className="w-5 h-5" />
                <span className="font-semibold">Privacy Protection Built-In</span>
              </div>
              <p className="text-purple-200 text-sm mt-2">
                Enterprise gets private-by-default. Free tier gets 3 private analyses/month. Pro & Business get unlimited privacy.
              </p>
            </div>
            
            {/* Billing Toggle */}
            <div className="inline-flex bg-slate-900/60 backdrop-blur-xl border border-slate-700/50 rounded-lg p-1 mb-12">
              <button 
                onClick={() => setBillingCycle('monthly')}
                className={`px-6 py-2 rounded-md font-medium transition-all ${
                  billingCycle === 'monthly' 
                    ? 'bg-blue-600 text-white' 
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                Monthly
              </button>
              <button 
                onClick={() => setBillingCycle('annual')}
                className={`px-6 py-2 rounded-md font-medium transition-all relative ${
                  billingCycle === 'annual' 
                    ? 'bg-blue-600 text-white' 
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                Annual
                <span className="absolute -top-3 -right-3 z-20 bg-green-500 text-white text-xs px-2 py-1 rounded-full">
                  Save 20%
                </span>
              </button>
            </div>
          </div>
        </section>

        {/* Pricing Cards */}
        <section className="pb-20 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 pricing-grid">
              {tierData.map((tier) => {
                const IconComponent = tier.icon;
                const isCurrentTier = currentTier === tier.key;
                const isUpgrading = upgrading === tier.key;
                
                return (
                  <div key={tier.key} className={`group relative ${tier.popular ? 'z-10 sm:scale-105' : ''}`}>
                    {tier.badge && (
                      <div className={`absolute -top-4 left-1/2 transform -translate-x-1/2 px-3 py-1 rounded-full text-xs font-bold z-20 ${
                        tier.popular 
                          ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white'
                          : tier.badge === 'ENTERPRISE'
                          ? 'bg-gradient-to-r from-purple-500 to-pink-600 text-white'
                          : 'bg-gradient-to-r from-green-500 to-emerald-600 text-white'
                      }`}>
                        {tier.badge}
                      </div>
                    )}
                    
                    <div className={`absolute -inset-1 rounded-2xl blur-sm opacity-0 group-hover:opacity-100 transition duration-500 bg-gradient-to-r ${
                      tier.popular 
                        ? 'from-blue-500/30 to-purple-500/30' 
                        : 'from-slate-700/30 to-slate-600/30'
                    }`} />
                    
                    <div className={`relative rounded-2xl p-6 h-full transition-all duration-300 flex flex-col ${
                      tier.popular
                        ? 'bg-slate-900/95 border-2 border-blue-500/50'
                        : isCurrentTier
                        ? 'bg-slate-900/90 border-2 border-green-500/50'
                        : 'bg-slate-900/70 border border-slate-700/50 hover:bg-slate-900/90'
                    }`}>
                      
                      {/* Privacy Badge */}
                      {tier.key === 'enterprise' && (
                        <div className="absolute top-4 right-4 bg-purple-500/20 text-purple-300 px-2 py-1 rounded-full text-xs font-medium border border-purple-500/30">
                          <Lock className="w-3 h-3 inline mr-1" />
                          Private
                        </div>
                      )}
                      
                      {/* Icon */}
                      <div className={`w-10 h-10 rounded-xl bg-gradient-to-r ${tier.iconColor} flex items-center justify-center mb-4`}>
                        <IconComponent className="w-5 h-5 text-white" />
                      </div>
                      
                      {/* Title & Description */}
                      <h3 className="text-lg font-bold text-white mb-1">{tier.config.name}</h3>
                      <p className="text-slate-400 mb-4 text-xs">{tier.description}</p>
                      
                      {/* Value proposition */}
                      {tier.valueProps && (
                        <p className="text-green-400 text-xs mb-3 font-medium">
                          {tier.valueProps}
                        </p>
                      )}
                      
                      {/* Price */}
                      <div className="mb-4">
                        <span className="text-3xl font-black text-white">{getPrice(tier.config)}</span>
                        {tier.config.price > 0 && (
                          <>
                            <span className="text-slate-400 text-xs ml-1">
                              /{billingCycle === 'annual' ? 'year' : 'month'}
                            </span>
                            {billingCycle === 'annual' && (
                              <div className="text-green-400 text-xs mt-1">
                                Save ${Math.floor(tier.config.price * 0.2 * 12)}/year
                              </div>
                            )}
                          </>
                        )}
                      </div>
                      
                      {/* Features */}
                      <ul className="space-y-2 mb-6 flex-grow">
                        {tier.features.map((feature, featureIndex) => (
                          <li key={featureIndex} className="flex items-start space-x-2">
                            <CheckCircle className="w-3.5 h-3.5 text-green-400 mt-0.5 flex-shrink-0" />
                            <span className="text-slate-300 text-xs leading-tight">{feature}</span>
                          </li>
                        ))}
                      </ul>
                      
                      {/* Privacy Highlight */}
                      <div className={`mb-4 p-2 rounded-lg text-xs ${
                        tier.key === 'observer' 
                          ? 'bg-yellow-500/10 border border-yellow-500/30 text-yellow-300'
                          : tier.key === 'enterprise'
                          ? 'bg-purple-500/10 border border-purple-500/30 text-purple-300'
                          : 'bg-green-500/10 border border-green-500/30 text-green-300'
                      }`}>
                        <div className="flex items-center gap-1">
                          <Lock className="w-3 h-3" />
                          <span className="font-medium">
                            {tier.key === 'observer' ? 'Limited Privacy' :
                             tier.key === 'enterprise' ? 'Private by Default' : 'Full Privacy Control'}
                          </span>
                        </div>
                      </div>
                      
                      {/* CTA Button */}
                      <button
                        onClick={() => handleUpgrade(tier.key)}
                        disabled={isCurrentTier || isUpgrading}
                        className={`w-full text-center py-2.5 rounded-lg font-semibold text-sm transition-all ${
                          isCurrentTier
                            ? 'bg-green-600 text-white cursor-default'
                            : tier.popular
                            ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white hover:from-blue-600 hover:to-purple-700 disabled:opacity-50'
                            : tier.key === 'enterprise'
                            ? 'bg-gradient-to-r from-purple-500 to-pink-600 text-white hover:from-purple-600 hover:to-pink-700'
                            : tier.key === 'business'
                            ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:from-green-600 hover:to-emerald-700'
                            : 'bg-slate-800 text-white hover:bg-slate-700 disabled:opacity-50'
                        }`}
                      >
                        {isUpgrading ? (
                          <div className="flex items-center justify-center gap-2">
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            Processing...
                          </div>
                        ) : (
                          tier.cta
                        )}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Privacy Guarantee Section */}
        <section className="py-12 px-4 bg-gradient-to-r from-purple-900/20 to-blue-900/20">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-white mb-4">
                <Lock className="w-8 h-8 inline mr-3" />
                Privacy Guaranteed
              </h2>
              <p className="text-xl text-slate-300">
                Your competitive intelligence is protected at every tier
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-8 h-8 text-green-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Bank-Level Security</h3>
                <p className="text-slate-400 text-sm">
                  256-bit encryption, SOC 2 compliance, and zero-trust architecture
                </p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-purple-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Eye className="w-8 h-8 text-purple-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Your Choice, Your Control</h3>
                <p className="text-slate-400 text-sm">
                  Decide per analysis: keep competitive research private or share insights publicly
                </p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-blue-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Team Privacy</h3>
                <p className="text-slate-400 text-sm">
                  Enterprise teams get private-by-default with granular sharing controls
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Trust Indicators */}
        <section className="py-12 px-4 bg-slate-900/30">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              <div>
                <div className="text-3xl font-bold text-white mb-2">95%</div>
                <p className="text-slate-400 text-sm">AI Accuracy Rate</p>
              </div>
              <div>
                <div className="text-3xl font-bold text-white mb-2">2.1 sec</div>
                <p className="text-slate-400 text-sm">Average Analysis Time</p>
              </div>
              <div>
                <div className="text-3xl font-bold text-white mb-2">40+ hrs</div>
                <p className="text-slate-400 text-sm">Saved Monthly</p>
              </div>
              <div>
                <div className="text-3xl font-bold text-white mb-2">$2.4M</div>
                <p className="text-slate-400 text-sm">ROI Generated</p>
              </div>
            </div>
          </div>
        </section>

        {/* What's Included */}
        <section className="py-20 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-white mb-6">
                Everything You Get
              </h2>
              <p className="text-xl text-slate-400 max-w-3xl mx-auto">
                All plans include our core AI analysis engine. Higher tiers unlock more power and privacy.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              <div className="bg-slate-900/60 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-8">
                <BarChart3 className="w-12 h-12 text-blue-400 mb-6" />
                <h3 className="text-xl font-bold text-white mb-4">5 AI Agents</h3>
                <p className="text-slate-400">
                  Our multi-agent system analyzes design, performance, SEO, UX, and business impact simultaneously.
                </p>
              </div>

              <div className="bg-slate-900/60 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-8">
                <Target className="w-12 h-12 text-green-400 mb-6" />
                <h3 className="text-xl font-bold text-white mb-4">ROI Predictions</h3>
                <p className="text-slate-400">
                  See exactly how improvements will impact your revenue with data-backed predictions.
                </p>
              </div>

              <div className="bg-slate-900/60 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-8">
                <Lock className="w-12 h-12 text-purple-400 mb-6" />
                <h3 className="text-xl font-bold text-white mb-4">Privacy Controls</h3>
                <p className="text-slate-400">
                  Keep competitive research private or share insights publicly - your choice, your control.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 px-4 bg-slate-900/30">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-12">Common Questions</h2>
            
            <div className="space-y-4 text-left">
              <div className="bg-slate-900/50 rounded-xl p-6 border border-slate-700/50">
                <h3 className="text-white font-semibold mb-2">What's the difference between public and private analyses?</h3>
                <p className="text-slate-400">Public analyses are shared with the community to help improve our AI. Private analyses are kept completely confidential - perfect for analyzing competitors or internal projects.</p>
              </div>
              
              <div className="bg-slate-900/50 rounded-xl p-6 border border-slate-700/50">
                <h3 className="text-white font-semibold mb-2">Why should I upgrade from Observer?</h3>
                <p className="text-slate-400">The free Observer tier is limited to 10 analyses per month with only 3 private analyses. Professional gives you unlimited analyses plus advanced AI insights that provide 10x more value per analysis.</p>
              </div>
              
              <div className="bg-slate-900/50 rounded-xl p-6 border border-slate-700/50">
                <h3 className="text-white font-semibold mb-2">Is my data secure?</h3>
                <p className="text-slate-400">Absolutely. We use bank-level 256-bit encryption, SOC 2 compliance, and enterprise-grade security. Private analyses are never shared or used for training our AI models.</p>
              </div>
              
              <div className="bg-slate-900/50 rounded-xl p-6 border border-slate-700/50">
                <h3 className="text-white font-semibold mb-2">Can I change plans anytime?</h3>
                <p className="text-slate-400">Yes! Upgrade or downgrade anytime. Changes take effect immediately and we'll prorate your billing.</p>
              </div>

              <div className="bg-slate-900/50 rounded-xl p-6 border border-slate-700/50">
                <h3 className="text-white font-semibold mb-2">What payment methods do you accept?</h3>
                <p className="text-slate-400">We accept all major credit cards, debit cards, and ACH transfers for annual plans. Enterprise customers can pay by invoice.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-20 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 rounded-3xl blur-xl opacity-30" />
              <div className="relative bg-slate-900/90 backdrop-blur-xl border border-slate-700/50 rounded-3xl p-12">
                <Lock className="w-12 h-12 text-green-400 mx-auto mb-6" />
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                  Start Analyzing Securely Today
                </h2>
                <p className="text-xl text-slate-400 mb-8 max-w-2xl mx-auto">
                  Every day without proper website analysis is potential revenue lost. 
                  Join the companies already seeing results with complete privacy control.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link
                    to="/register?tier=professional"
                    className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-8 py-4 rounded-xl font-semibold hover:from-blue-600 hover:to-purple-700 transition-all flex items-center justify-center gap-2"
                  >
                    <span>Get Professional</span>
                    <ArrowRight className="w-5 h-5" />
                  </Link>
                  <Link
                    to="/register?tier=observer"
                    className="border border-slate-600 text-white px-8 py-4 rounded-xl font-semibold hover:bg-slate-800/50 transition-all flex items-center justify-center gap-2"
                  >
                    <span>Start Free</span>
                    <Eye className="w-5 h-5" />
                  </Link>
                </div>
                
                <div className="flex items-center justify-center gap-6 mt-8 text-slate-500 text-sm">
                  <div className="flex items-center gap-2">
                    <CreditCard className="w-4 h-4" />
                    <span>Secure Payment</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                    <span>30-Day Guarantee</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Lock className="w-4 h-4" />
                    <span>Privacy Protected</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}